/**
 * Audio Manager for handling Chrome's autoplay policy
 * Requires user interaction before audio can be played
 * Implements one-time permission popup with localStorage persistence
 */

class AudioManager {
  constructor() {
    this.audioContext = null;
    this.audioBuffer = null;
    this.isAudioEnabled = false;
    this.hasUserInteracted = false;
    this.audioPath = "/confirmation.mp3";
    this.fallbackAudio = null;
    this.permissionKey = "audioNotificationPermission";
    this.hasShownPermissionPopup = false;

    // Check localStorage for existing permission
    this.checkStoredPermission();

    // Initialize audio on user interaction
    this.initializeOnUserInteraction();
  }

  /**
   * Check if permission has already been requested/stored
   */
  checkStoredPermission() {
    const storedPermission = localStorage.getItem(this.permissionKey);
    if (storedPermission === "granted") {
      this.hasUserInteracted = true;
      this.isAudioEnabled = true;

    } else if (storedPermission === "denied") {
      this.hasShownPermissionPopup = true;

    }
  }

  /**
   * Store permission decision in localStorage
   */
  storePermissionDecision(decision) {
    localStorage.setItem(this.permissionKey, decision);
    this.hasShownPermissionPopup = true;

  }

  /**
   * Check if permission popup should be shown
   */
  shouldShowPermissionPopup() {
    return !this.hasShownPermissionPopup && !this.hasUserInteracted;
  }

  /**
   * Mark permission popup as shown
   */
  markPermissionPopupShown() {
    this.hasShownPermissionPopup = true;
  }

  /**
   * Initialize audio after user interaction
   */
  initializeOnUserInteraction() {
    const enableAudio = () => {
      if (!this.hasUserInteracted) {
        this.hasUserInteracted = true;
        this.storePermissionDecision("granted");
        this.initializeAudio();


        // Remove event listeners after first interaction
        document.removeEventListener("click", enableAudio);
        document.removeEventListener("keydown", enableAudio);
        document.removeEventListener("touchstart", enableAudio);
      }
    };

    // Listen for any user interaction
    document.addEventListener("click", enableAudio);
    document.addEventListener("keydown", enableAudio);
    document.addEventListener("touchstart", enableAudio);
  }

  /**
   * Initialize Web Audio API context and preload audio
   */
  async initializeAudio() {
    try {
      // Create AudioContext
      this.audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();

      // Resume context if suspended
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }

      // Load and decode audio
      await this.loadAudio();

      // Create fallback HTML5 audio
      this.fallbackAudio = new Audio(this.audioPath);
      this.fallbackAudio.preload = "auto";

      this.isAudioEnabled = true;

    } catch (error) {
      console.warn("🔊⚠️ Audio initialization failed:", error);
      // Fallback to HTML5 audio only
      this.initializeFallbackAudio();
    }
  }

  /**
   * Load and decode audio file
   */
  async loadAudio() {
    try {
      const response = await fetch(this.audioPath);
      const arrayBuffer = await response.arrayBuffer();
      this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
    } catch (error) {
      console.warn("🔊⚠️ Audio loading failed:", error);
      throw error;
    }
  }

  /**
   * Initialize fallback HTML5 audio
   */
  initializeFallbackAudio() {
    try {
      this.fallbackAudio = new Audio(this.audioPath);
      this.fallbackAudio.preload = "auto";
      this.isAudioEnabled = true;

    } catch (error) {
      console.error("🔊❌ Fallback audio initialization failed:", error);
    }
  }

  /**
   * Play confirmation sound
   * @param {Object} options - Play options
   * @param {number} options.volume - Volume level (0-1)
   * @param {Function} options.onSuccess - Success callback
   * @param {Function} options.onError - Error callback
   */
  async playConfirmation(options = {}) {
    const { volume = 0.5, onSuccess, onError } = options;

    if (!this.hasUserInteracted) {
      console.warn("🔊⚠️ Cannot play audio: No user interaction yet");
      this.showVisualNotification();
      onError?.(new Error("No user interaction"));
      return;
    }

    if (!this.isAudioEnabled) {
      console.warn("🔊⚠️ Audio not enabled");
      this.showVisualNotification();
      onError?.(new Error("Audio not enabled"));
      return;
    }

    try {
      // Try Web Audio API first
      if (this.audioContext && this.audioBuffer) {
        await this.playWithWebAudio(volume);

        onSuccess?.();
        return;
      }

      // Fallback to HTML5 audio
      if (this.fallbackAudio) {
        await this.playWithHtml5Audio(volume);

        onSuccess?.();
        return;
      }

      throw new Error("No audio playback method available");
    } catch (error) {
      console.warn("🔊❌ Audio playback failed:", error);
      this.showVisualNotification();
      onError?.(error);
    }
  }

  /**
   * Play audio using Web Audio API
   */
  async playWithWebAudio(volume) {
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }

    const source = this.audioContext.createBufferSource();
    const gainNode = this.audioContext.createGain();

    source.buffer = this.audioBuffer;
    gainNode.gain.value = volume;

    source.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    source.start();
  }

  /**
   * Play audio using HTML5 Audio
   */
  async playWithHtml5Audio(volume) {
    this.fallbackAudio.volume = volume;
    await this.fallbackAudio.play();
  }

  /**
   * Show visual notification when audio can't play
   */
  showVisualNotification() {
    // Create visual notification
    const notification = document.createElement("div");
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4CAF50;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      animation: slideIn 0.3s ease-out;
    `;

    notification.innerHTML = "🔔 New notification received!";

    // Add animation
    const style = document.createElement("style");
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(notification);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.animation = "slideIn 0.3s ease-out reverse";
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  /**
   * Check if audio can be played
   */
  canPlayAudio() {
    return this.hasUserInteracted && this.isAudioEnabled;
  }

  /**
   * Get audio status
   */
  getStatus() {
    return {
      hasUserInteracted: this.hasUserInteracted,
      isAudioEnabled: this.isAudioEnabled,
      canPlay: this.canPlayAudio(),
      audioContext: !!this.audioContext,
      audioBuffer: !!this.audioBuffer,
      fallbackAudio: !!this.fallbackAudio,
      shouldShowPermissionPopup: this.shouldShowPermissionPopup(),
      hasShownPermissionPopup: this.hasShownPermissionPopup,
      permissionStatus:
        localStorage.getItem(this.permissionKey) || "not_requested",
    };
  }

  /**
   * Enable audio notifications
   */
  async enableAudio() {
    try {
      this.hasUserInteracted = true;
      this.storePermissionDecision("granted");
      await this.initializeAudio();
      return true;
    } catch (error) {
      console.error("🔊 Failed to enable audio:", error);
      return false;
    }
  }

  /**
   * Disable audio notifications
   */
  disableAudio() {
    this.storePermissionDecision("denied");
    this.isAudioEnabled = false;
    this.hasUserInteracted = false;

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    if (this.fallbackAudio) {
      this.fallbackAudio.pause();
      this.fallbackAudio = null;
    }


  }

  /**
   * Reset audio permission (for testing or user preference change)
   */
  resetPermission() {
    localStorage.removeItem(this.permissionKey);
    this.hasShownPermissionPopup = false;
    this.hasUserInteracted = false;
    this.isAudioEnabled = false;

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    if (this.fallbackAudio) {
      this.fallbackAudio.pause();
      this.fallbackAudio = null;
    }


  }
}

// Create singleton instance
const audioManager = new AudioManager();

export default audioManager;
